import dayjs from "dayjs";
import { isImageUrl } from "../chatAttachments";

/* ────────────────────────────────────────────────────────────
 * messageStatus — pure helpers for a chat message's lifecycle:
 * deleted (QA CM-03) and edited (QA CM-05). Shared by AppState's
 * socket handlers, MessageList, RepliesTo and useHeldDelete so every
 * place reads a message's state the same way.
 * ──────────────────────────────────────────────────────────── */

// How long a confirmed delete is held on this side, with Undo, before
// the `is_deleted` socket event goes out. The server has no restore.
export const UNDO_DELETE_MS = 5000;

// History rows carry Team_Message_Id, live rows message_id.
export const messageKey = (m) => String(m?.Team_Message_Id ?? m?.message_id);

const isOn = (value) => value === true || value === 1 || value === "1";

// `_deleted` is set here (markDeleted); IsDeleted / is_deleted are what a
// server tombstone is expected to carry once history keeps deleted rows.
export const isDeletedMessage = (m) =>
  Boolean(m) && (m._deleted === true || isOn(m.IsDeleted) || isOn(m.is_deleted));

const hasText = (m) =>
  Boolean(
    (m.Team_Message_Text && String(m.Team_Message_Text).trim().length) ||
      (m.message && String(m.message).trim().length)
  );

// One or more photos with no text and no other file. Only such rows join
// a collage (MessageList's computeBurstAnchors), so only they fold into
// one "Message deleted" row once deleted. Live rows carry `files`,
// history rows one `Resource_Path`.
export const isPhotoOnlyMessage = (m) => {
  if (!m) return false;
  const attached =
    Array.isArray(m.files) && m.files.length
      ? m.files
      : m.Resource_Path
      ? [m.Resource_Path]
      : [];
  return attached.length > 0 && attached.every(isImageUrl) && !hasText(m);
};

// The same for a row that may already be deleted: markDeleted empties the
// row, so it keeps the answer in `_photoOnly`.
export const wasPhotoOnlyMessage = (m) =>
  Boolean(m) && (m._photoOnly === true || isPhotoOnlyMessage(m));

// Keeps the ids, sender, names and time; drops everything that was said
// or sent, so nothing of it can render or be quoted any more. `_photoOnly`
// is a yes/no, not the photo: it lets a deleted album fold into one row
// for everyone, not only for the sender, whose rows carry a delete group.
export const markDeleted = (m) => ({
  ...m,
  _deleted: true,
  _photoOnly: wasPhotoOnlyMessage(m),
  Team_Message_Text: "",
  message: "",
  Resource_Path: null,
  files: [],
  reactions: [],
});

// IsEdit has been read since 2023; a strict check keeps React from
// printing a literal 0 if the server ever sends IsEdit: 0.
export const isEditedMessage = (m) =>
  Boolean(m) &&
  (m.IsEdit === true ||
    m.IsEdit === 1 ||
    m.IsEdit === "1" ||
    m.IsEdit === "true" ||
    m.is_edited === true ||
    m.is_edited === 1);

// When the message was last edited, or null when nothing readable is set.
export const editedAt = (m) => {
  const candidates = [m?.EditedOn, m?.edited_at];
  for (const value of candidates) {
    if (value && dayjs(value).isValid()) return value;
  }
  return null;
};

// Applies a `message-edited` event to a row. `textField` is
// Team_Message_Text for history rows and message for live ones. The
// payload is only known to carry message_id and message, so the time the
// event arrived stands in for the edit time until the server sends one.
export const markEdited = (m, data, textField) => ({
  ...m,
  [textField]: data.message,
  IsEdit: true,
  EditedOn:
    data.EditedOn || data.edited_at || data.updated_at || new Date().toISOString(),
});
